// B6 (docs/build-briefs/B6-barcode-receive.md). The pure rules behind "scan a barcode to receive": the scanned list, how a scan
// adds to it, how the list becomes receipts on the open purchase order, and what the link sheet lists. No DOM here, so every rule
// is tested on its own (test/b6-barcode-receive-model.test.ts). ui/src/barcodeReceive.ts draws what this decides.
//
// The list counts BOTTLES. A bottle code adds 1, a case code adds the item's case size. Nothing is posted until Finish.

import type { BarcodeItemDto, BarcodeMatchDto, BarcodeReceiveAs } from "./api.ts";
import { itemKindLabel } from "./catalogDisplay.ts";
import { compareNames } from "./nameSort.ts";

export type StepUnit = "bottle" | "case";

export interface ScanRow {
  productId: string;
  name: string;
  receiveAs: BarcodeReceiveAs;
  caseSize: number | null;
  bottles: number;
  /** What the + and - on this row move by: one bottle or one full case. */
  stepUnit: StepUnit;
}

export const CASE_SIZE_UNKNOWN_TEXT = "No case size for this item";
export const HINT_TEXT = "Point at a barcode";
export const NOT_ON_ORDER_TEXT = "Not on this order";
export const TURNED_OFF_TEXT = "Turned off in Settings";

export interface AddResult {
  rows: ScanRow[];
  /** Set when nothing was added: the plain reason. */
  refused?: string;
}

/** A scan adds to the item's row, which moves to the top. A case code needs a case size; without one nothing is added. */
export function addScan(rows: readonly ScanRow[], match: BarcodeMatchDto): AddResult {
  if (!match.active) return { rows: [...rows], refused: TURNED_OFF_TEXT };
  const isCase = match.matchedBy === "case";
  if (isCase && (match.caseSize === null || match.caseSize < 1)) {
    return { rows: [...rows], refused: CASE_SIZE_UNKNOWN_TEXT };
  }
  const add = isCase ? (match.caseSize as number) : 1;
  const existing = rows.find((row) => row.productId === match.liquorProductId);
  const row: ScanRow = {
    productId: match.liquorProductId,
    name: match.name,
    receiveAs: match.receiveAs,
    caseSize: match.caseSize,
    bottles: (existing?.bottles ?? 0) + add,
    stepUnit: isCase ? "case" : "bottle",
  };
  return { rows: [row, ...rows.filter((r) => r.productId !== match.liquorProductId)] };
}

/** Adds one bottle of an item picked by hand (or just linked). */
export function addPicked(rows: readonly ScanRow[], item: BarcodeItemDto): ScanRow[] {
  return addScan(rows, {
    liquorProductId: item.id,
    name: item.name,
    itemKind: item.itemKind,
    active: true,
    matchedBy: "bottle",
    receiveAs: item.receiveAs,
    caseSize: item.caseSize,
  }).rows;
}

export function stepAmount(row: ScanRow): number {
  return row.stepUnit === "case" && row.caseSize !== null ? row.caseSize : 1;
}

/** + or - on a row. A row that reaches 0 or less is removed. */
export function stepRow(rows: readonly ScanRow[], productId: string, direction: 1 | -1): ScanRow[] {
  return rows.flatMap((row) => {
    if (row.productId !== productId) return [row];
    const bottles = row.bottles + direction * stepAmount(row);
    return bottles > 0 ? [{ ...row, bottles }] : [];
  });
}

export function setStepUnit(
  rows: readonly ScanRow[],
  productId: string,
  unit: StepUnit,
): ScanRow[] {
  return rows.map((row) =>
    row.productId === productId && (unit === "bottle" || row.caseSize !== null)
      ? { ...row, stepUnit: unit }
      : row,
  );
}

export function removeRow(rows: readonly ScanRow[], productId: string): ScanRow[] {
  return rows.filter((row) => row.productId !== productId);
}

export function itemsText(rows: readonly ScanRow[]): string {
  return rows.length === 0 ? "-" : `${rows.length} ${rows.length === 1 ? "item" : "items"}`;
}

export function bottlesText(rows: readonly ScanRow[]): string {
  const total = rows.reduce((sum, row) => sum + row.bottles, 0);
  return total === 0 ? "-" : `${total} ${total === 1 ? "bottle" : "bottles"}`;
}

export function chipLabelForCase(caseSize: number): string {
  return `Case of ${caseSize}`;
}

/** The same code seen on consecutive frames is one scan. After a code is accepted it is ignored until `gapMs` has passed with it
 * out of view, so holding a bottle steady adds one, and showing the next bottle adds another. A different code is accepted at
 * once. */
export function createScanGate(gapMs = 800): { accept(code: string, nowMs: number): boolean } {
  let lastCode: string | undefined;
  let lastSeen = 0;
  return {
    accept(code, nowMs) {
      const same = code === lastCode && nowMs - lastSeen <= gapMs;
      lastCode = code;
      lastSeen = nowMs;
      return !same;
    },
  };
}

// ---------------------------------------------------------------------
// Finish: the scanned list as receipts on the open order
// ---------------------------------------------------------------------

export interface OrderLineInfo {
  lineId: string;
  productId: string;
  /** In the line's own order unit, as the server returned it. */
  outstandingQuantity: string;
  bottlesPerOrderUnit: string | null;
  unitCostCentsAtPlacement: number | null;
}

export interface PlannedReceipt {
  productId: string;
  name: string;
  bottles: number;
  onOrder: boolean;
  idempotencyKey: string;
  /** The body for POST .../inventory/receipts, or the reason this row cannot be posted. */
  body?: Record<string, unknown>;
  error?: string;
}

/** The delivery as a number of the line's order units, exactly, or null when it is not a whole number of bottles' worth: 6
 * bottles of a 12 pack is "0.5", 7 is not representable and is refused rather than rounded. */
export function orderUnitsForBottles(bottles: number, pack: number): string | null {
  if (!Number.isInteger(bottles) || bottles < 1 || !Number.isInteger(pack) || pack < 1) return null;
  const scaled = (bottles * 10000) / pack;
  if (!Number.isInteger(scaled)) return null;
  const whole = Math.floor(scaled / 10000);
  const frac = String(scaled % 10000)
    .padStart(4, "0")
    .replace(/0+$/, "");
  return frac.length === 0 ? String(whole) : `${whole}.${frac}`;
}

/** The order's unit cost per bottle when it is a whole number of cents, else undefined (the cost is then left off). */
export function costPerBottleCents(line: OrderLineInfo): number | undefined {
  const cost = line.unitCostCentsAtPlacement;
  if (cost === null || !Number.isFinite(cost) || cost < 0) return undefined;
  const pack = Number(line.bottlesPerOrderUnit);
  const perBottle = Number.isFinite(pack) && pack > 1 ? cost / pack : cost;
  return Number.isInteger(perBottle) ? perBottle : undefined;
}

/** The order's cost per order unit (a case) in whole cents, else undefined. */
export function costPerOrderUnitCentsOf(line: OrderLineInfo): number | undefined {
  const cost = line.unitCostCentsAtPlacement;
  return cost !== null && Number.isInteger(cost) && cost >= 0 ? cost : undefined;
}

export function receiptKey(
  sessionId: string,
  productId: string,
  bottles: number,
  onOrder: boolean,
): string {
  return `b6-${sessionId}-${productId}-${bottles}-${onOrder ? "o" : "x"}`;
}

function bottleCountBody(row: ScanRow, costCents: number | undefined): Record<string, unknown> {
  if (row.receiveAs === "container") {
    return {
      receivedContainerCount: String(row.bottles),
      ...(costCents === undefined ? {} : { costPerContainerCents: costCents }),
    };
  }
  return {
    quantity: String(row.bottles),
    unit: row.receiveAs === "unit" ? "unit" : "bottle",
    ...(costCents === undefined ? {} : { unitCostCents: costCents }),
  };
}

/** Turns the scanned rows into receipts. A row whose item is on a line of the order with something left goes to that line; a
 * row whose item is on no line of the order goes to the order's vendor with no line. Nothing is guessed: a row that cannot be
 * expressed exactly carries an `error` instead of a body. */
export function planReceipts(
  rows: readonly ScanRow[],
  lines: readonly OrderLineInfo[],
  order: { vendorId: string; vendorName: string },
  sessionId: string,
): PlannedReceipt[] {
  return rows.map((row) => {
    const productLines = lines.filter((line) => line.productId === row.productId);
    const line = productLines.find((candidate) => Number(candidate.outstandingQuantity) > 0);
    const base = { productId: row.productId, name: row.name, bottles: row.bottles };
    if (productLines.length > 0 && line === undefined) {
      return {
        ...base,
        onOrder: true,
        idempotencyKey: receiptKey(sessionId, row.productId, row.bottles, true),
        error: "Already received in full",
      };
    }
    if (line === undefined) {
      return {
        ...base,
        onOrder: false,
        idempotencyKey: receiptKey(sessionId, row.productId, row.bottles, false),
        body: {
          idempotencyKey: receiptKey(sessionId, row.productId, row.bottles, false),
          liquorProductId: row.productId,
          vendorId: order.vendorId,
          note: `Not on the order from ${order.vendorName}`,
          ...bottleCountBody(row, undefined),
        },
      };
    }
    const key = receiptKey(sessionId, row.productId, row.bottles, true);
    const pack = Number(line.bottlesPerOrderUnit);
    const common = {
      idempotencyKey: key,
      liquorProductId: row.productId,
      purchaseOrderLineId: line.lineId,
    };
    const cost = costPerBottleCents(line);
    // CASECOST1: a case is costed as invoiced (the order's cost per case), never as a rounded cost per bottle.
    const orderUnitCost = costPerOrderUnitCentsOf(line);
    if (Number.isFinite(pack) && pack > 1) {
      const units = orderUnitsForBottles(row.bottles, pack);
      if (units === null) {
        return {
          ...base,
          onOrder: true,
          idempotencyKey: key,
          error: `${row.bottles} bottles is not a whole share of a case of ${pack}`,
        };
      }
      return {
        ...base,
        onOrder: true,
        idempotencyKey: key,
        body: {
          ...common,
          receivedOrderUnits: units,
          ...(orderUnitCost === undefined ? {} : { costPerOrderUnitCents: orderUnitCost }),
        },
      };
    }
    return {
      ...base,
      onOrder: true,
      idempotencyKey: key,
      body: { ...common, ...bottleCountBody(row, cost) },
    };
  });
}

// ---------------------------------------------------------------------
// The link sheet
// ---------------------------------------------------------------------

export interface LinkFilter {
  /** An item kind, or null for All. */
  kind: string | null;
  /** One letter, or null for every letter. */
  letter: string | null;
}

export function letterOf(name: string): string {
  const first = name.trim().charAt(0).toLocaleUpperCase();
  return /\p{L}/u.test(first) ? first : "#";
}

export interface LinkSheetView {
  kinds: { value: string | null; label: string }[];
  letters: string[];
  items: BarcodeItemDto[];
}

/** Chips for the kinds that exist (plus All), the letters of the items the kind chip leaves, and the items themselves A to Z. */
export function linkSheetView(items: readonly BarcodeItemDto[], filter: LinkFilter): LinkSheetView {
  const kindValues = [...new Set(items.map((item) => item.itemKind))].sort((a, b) =>
    compareNames(itemKindLabel(a), itemKindLabel(b)),
  );
  const byKind =
    filter.kind === null ? [...items] : items.filter((item) => item.itemKind === filter.kind);
  const letters = [...new Set(byKind.map((item) => letterOf(item.name)))].sort(compareNames);
  const shown = (
    filter.letter === null ? byKind : byKind.filter((item) => letterOf(item.name) === filter.letter)
  ).sort((a, b) => compareNames(a.name, b.name) || a.id.localeCompare(b.id));
  return {
    kinds: [
      ...kindValues.map((value) => ({
        value: value as string | null,
        label: itemKindLabel(value),
      })),
      { value: null, label: "All" },
    ],
    letters,
    items: shown,
  };
}

export function itemCodeNote(item: BarcodeItemDto, kind: StepUnit): string {
  const has = kind === "bottle" ? item.barcode !== null : item.caseBarcode !== null;
  return has ? "Has a barcode" : "No barcode yet";
}

/** The code kinds an item can take: a case code only once the item has a case size. */
export function linkableKinds(item: BarcodeItemDto): StepUnit[] {
  return item.caseSize === null ? ["bottle"] : ["bottle", "case"];
}

/** Grouped for the unit tag on a row: "12 bottles" or "2 cases". */
export function rowQuantityNote(row: ScanRow): string {
  if (row.caseSize !== null && row.bottles % row.caseSize === 0 && row.bottles >= row.caseSize) {
    const cases = row.bottles / row.caseSize;
    return `${cases} ${cases === 1 ? "case" : "cases"}`;
  }
  return row.bottles === 1 ? "Bottle" : "Bottles";
}
